import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Typography,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon as MenuListItemIcon,
  Tooltip,
  Badge,
  TextField,
  InputAdornment,
} from '@mui/material';
import { 
  Add as AddIcon, 
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Delete as DeleteIcon,
  DriveFileMove as MoveThreadIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { capitalizeFirstLetter, getFirstAlphabeticChar, getAvatarColor } from '../helpers/stringUtils';
import { formatRelativeTime, formatDetailedTooltip } from '../helpers/dateUtils';
import { useChannelAdmin } from '../hooks/useChannelAdmin';

// API Response types for thread list
export interface ApiThread {
  id: string;
  title: string;
  description: string;
  message_count: number;
  last_message_at: string | null;
  email_thread_id: string | null;
  original_message_id: string | null;
  email_type: string | null;
  from_email_id: string;
  created_at: string;
  updated_at: string;
  // Legacy fields for backward compatibility
  channel_id?: string;
  is_active?: boolean;
  is_archived?: boolean;
  ai_enabled?: boolean;
  ai_summary?: string | null;
  // Read state from threads API for read/unread styling
  is_read?: boolean;
  /** Provider thread id (e.g. Gmail) for move-to-classification API; set when messages load or from API */
  external_app_id?: string | null;
}

/** Substring match on title, description, ai_summary — shared with ThreadDetailPage for message-channel client search */
export function messageThreadMatchesClientSearch(
  thread: ApiThread,
  searchText: string
): boolean {
  const q = searchText.trim().toLowerCase();
  if (q === '') return true;
  const haystack = [thread.title, thread.description, thread.ai_summary ?? '']
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

interface ThreadListProps {
  threads: ApiThread[];
  selectedThreadId: string | null;
  loading: boolean;
  threadsLoading: boolean;
  showArchivedThreads: boolean;
  /** True when archived filter/category is applied and archived threads are displayed */
  isViewingArchivedThreads?: boolean;
  onThreadSelect: (thread: ApiThread) => void;
  onNewThread: () => void;
  onComposeClick: () => void;
  onThreadMenuOpen: (event: React.MouseEvent<HTMLElement>, thread: ApiThread) => void;
  onShowArchivedChange: (showArchived: boolean) => void;
  onThreadEdit: (thread: ApiThread) => void;
  onThreadArchive: (thread: ApiThread) => void;
  onThreadDelete: (thread: ApiThread) => void;
  /** When provided and channel is message type, show Move Thread in menu; called with thread to move */
  onThreadMove?: (thread: ApiThread) => void;
  isChannelArchived?: boolean;
  channelType?: 'email' | 'message' | 'voice';
  messagingProvider?: 'regular' | 'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams';
  channelId?: string; // Channel ID for checking admin permissions
  // Pagination props
  currentPage: number;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  // Filter props
  onFilterClick?: () => void;
  activeFilterCount?: number;
  // Search props
  onSearch?: (searchText: string) => void;
  searchText?: string;
  /** Channel admin bulk select: ids currently checked in the list */
  bulkSelectedIds?: string[];
  /** Toggle one thread in bulk selection (checkbox); list row click unchanged */
  onBulkToggle?: (threadId: string, checked: boolean) => void;
  /** Select/deselect all threads currently visible in the list (same behavior as bulk detail pane) */
  onBulkSelectAllVisible?: () => void;
  /** Checked/indeterminate state for visible rows — must match parent’s visibleBulkThreadIds logic */
  bulkSelectAllCheckbox?: { checked: boolean; indeterminate: boolean };
}

/** MIME type for drag-and-drop move thread to category */
export const THREAD_MOVE_DRAG_TYPE = 'application/x-shay-thread-move';

/** Width reserved for bulk checkbox when visible; collapsed to 0 when hidden so row content shifts left */
const BULK_CHECKBOX_COLUMN_WIDTH_PX = 42;

const ThreadList: React.FC<ThreadListProps> = ({
  threads,
  selectedThreadId,
  loading,
  threadsLoading,
  showArchivedThreads,
  isViewingArchivedThreads = false,
  onThreadSelect,
  onNewThread,
  onComposeClick,
  onThreadMenuOpen,
  onShowArchivedChange,
  onThreadEdit,
  onThreadArchive,
  onThreadDelete,
  onThreadMove,
  isChannelArchived = false,
  channelType = 'email',
  messagingProvider,
  channelId,
  // Pagination props
  currentPage,
  hasMore,
  loadingMore,
  onLoadMore,
  // Filter props
  onFilterClick,
  activeFilterCount = 0,
  // Search props
  onSearch,
  searchText = '',
  bulkSelectedIds = [],
  onBulkToggle,
  onBulkSelectAllVisible,
  bulkSelectAllCheckbox,
}) => {
  const muiTheme = useTheme();
  
  // Check if current user is channel admin (use channelId from prop or from first thread)
  const effectiveChannelId = channelId || threads[0]?.channel_id;
  const { isChannelAdmin } = useChannelAdmin(effectiveChannelId);
  
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedThread, setSelectedThread] = useState<ApiThread | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Search state
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [localSearchText, setLocalSearchText] = useState<string>(searchText);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Deduplicate by id so key={thread.id} stays unique (first occurrence kept)
  const uniqueThreads = useMemo(() => {
    const seen = new Set<string>();
    return threads.filter(t => {
      if (seen.has(t.id)) return false;
      seen.add(t.id);
      return true;
    });
  }, [threads]);

  // Message channels only: list API ignores search/body param — filter loaded rows locally so search matches email UX without affecting email/voice
  const displayThreads = useMemo(() => {
    if (channelType !== 'message' || searchText.trim() === '') {
      return uniqueThreads;
    }
    return uniqueThreads.filter((t) => messageThreadMatchesClientSearch(t, searchText));
  }, [uniqueThreads, channelType, searchText]);

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, thread: ApiThread) => {
    event.stopPropagation(); // Prevent thread selection when clicking menu
    setMenuAnchorEl(event.currentTarget);
    setSelectedThread(thread);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedThread(null);
  };

  // Handle menu actions
  const handleEdit = () => {
    if (selectedThread) {
      onThreadEdit(selectedThread);
    }
    handleMenuClose();
  };

  const handleArchive = () => {
    if (selectedThread) {
      // If we're viewing archived threads, ensure the thread is treated as archived
      // This handles cases where thread.is_archived might not be set correctly
      const isArchived = selectedThread.is_archived || showArchivedThreads || isViewingArchivedThreads;
      const threadToProcess = isArchived && !selectedThread.is_archived
        ? { ...selectedThread, is_archived: true }
        : selectedThread;
      onThreadArchive(threadToProcess);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedThread) {
      onThreadDelete(selectedThread);
    }
    handleMenuClose();
  };

  const handleMoveThread = () => {
    if (selectedThread && onThreadMove) {
      onThreadMove(selectedThread);
    }
    handleMenuClose();
  };

  // Handle scroll to detect when user reaches bottom
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || loadingMore || !hasMore || isLoadingRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100; // Load more when 100px from bottom

    if (scrollHeight - scrollTop - clientHeight < threshold) {
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce the API call to prevent multiple rapid calls
      scrollTimeoutRef.current = setTimeout(() => {
        if (!isLoadingRef.current && !loadingMore && hasMore) {
          isLoadingRef.current = true;
          onLoadMore();
        }
      }, 150); // 150ms debounce
    }
  }, [loadingMore, hasMore, onLoadMore]);

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Reset loading ref when loadingMore changes; after load more, check if viewport needs more to fill. Do not scroll to bottom – scroll bar stays at top.
  useEffect(() => {
    if (!loadingMore) {
      isLoadingRef.current = false;
      if (hasMore) {
        const timeoutId = setTimeout(() => {
          const container = scrollContainerRef.current;
          if (container && container.scrollHeight <= container.clientHeight) {
            onLoadMore();
          }
        }, 500);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [loadingMore, hasMore, onLoadMore]);

  // When we switch category or list is first loaded/refetched: scroll bar always at top
  const prevThreadsLoadingRef = useRef<boolean>(false);
  useEffect(() => {
    const wasLoading = prevThreadsLoadingRef.current;
    prevThreadsLoadingRef.current = threadsLoading;
    if (wasLoading && !threadsLoading) {
      const container = scrollContainerRef.current;
      if (container) {
        container.scrollTop = 0;
      }
    }
  }, [threadsLoading]);

  // On mount: scroll container starts at top
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = 0;
    }
  }, []);

  // Sync local search text with prop
  useEffect(() => {
    setLocalSearchText(searchText);
    setIsSearchOpen(searchText.trim() !== '');
  }, [searchText]);

  // After first bulk selection, every row checkbox stays visible; before that, only on row hover
  const anyBulkThreadSelected = bulkSelectedIds.length > 0;

  // Second row below compose/search header: “select all” only after at least one thread is checked
  const showBulkSelectAllRow =
    isChannelAdmin &&
    !!onBulkToggle &&
    bulkSelectedIds.length > 0 &&
    !!onBulkSelectAllVisible &&
    !!bulkSelectAllCheckbox;

  // Focus search input when search box opens
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Handle search icon click
  const handleSearchClick = () => {
    setIsSearchOpen(true);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalSearchText(value);
    // Call onSearch callback with debounce
    if (onSearch) {
      onSearch(value);
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setLocalSearchText('');
    setIsSearchOpen(false);
    if (onSearch) {
      onSearch('');
    }
  };

  // Initial check to fill viewport if needed
  useEffect(() => {
    if (!loading && !threadsLoading && hasMore && threads.length > 0) {
      const container = scrollContainerRef.current;
      if (container && container.scrollHeight <= container.clientHeight) {
        onLoadMore();
      }
    }
  }, [loading, threadsLoading, hasMore, threads.length, onLoadMore]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Function to get thread icon - return Avatar with first character of from_email_id
  const getThreadIcon = (fromEmailId?: string) => {
    const firstChar = getFirstAlphabeticChar(fromEmailId || '');
    const avatarColor = getAvatarColor(fromEmailId || '');
    
    return (
      <Avatar
        sx={{
          bgcolor: avatarColor,
          width: 32,
          height: 32,
          fontSize: '1rem',
          fontWeight: 'bold',
        }}
      >
        {firstChar}
      </Avatar>
    );
  };

  return (
    <Box sx={{ 
      width: '100%', 
      borderRight: `1px solid #f0f0f0`, 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%'
    }}>
      {/* Header with Compose/New Thread, Search, and Filter Button */}
      <Box sx={{ px: 1.5, py: 0.6, borderBottom: `1px solid ${muiTheme.palette.divider}`, position: 'relative', overflow: 'hidden', height: '40px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, position: 'relative', height: '32px' }}>
          {/* Compose/New Thread button - hidden when search is open */}
          {!isSearchOpen && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'absolute', left: 0, zIndex: 1 }}>
              {channelType === 'email' ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={onComposeClick}
                  sx={{
                    backgroundColor: muiTheme.palette.primary.main,
                    fontSize: '0.75rem',
                    padding: '4px 12px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: muiTheme.palette.primary.dark,
                    },
                  }}
                >
                  Compose
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={onNewThread}
                  sx={{
                    backgroundColor: muiTheme.palette.primary.main,
                    fontSize: '0.75rem',
                    padding: '4px 12px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: muiTheme.palette.primary.dark,
                    },
                  }}
                >
                  {messagingProvider === 'whatsapp' ? 'NEW CHAT' : 'NEW THREAD'}
                </Button>
              )}
            </Box>
          )}

          {/* Search box - shows when search icon is clicked */}
          {isSearchOpen && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', position: 'absolute', left: 0, right: onFilterClick ? '40px' : '0', zIndex: 2 }}>
              <TextField
                inputRef={searchInputRef}
                fullWidth
                size="small"
                placeholder="Search threads..."
                value={localSearchText}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: muiTheme.palette.text.secondary, fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                  // Always show close button when search is open, even if empty, to allow collapsing
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{ padding: '4px' }}
                      >
                        <CloseIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                    paddingRight: localSearchText ? '8px' : '14px',
                  },
                }}
              />
            </Box>
          )}

          {/* Search and Filter icons - positioned absolutely on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'absolute', right: 0, zIndex: 3 }}>
            {/* Search icon button - show for email and message channels, positioned to the left of filter */}
            {!isSearchOpen && (
              <Tooltip title="Search threads">
                <IconButton
                  onClick={handleSearchClick}
                  size="small"
                  sx={{
                    color: muiTheme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: muiTheme.palette.action.hover,
                    },
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Filter button - show for email and message channels */}
            {onFilterClick && (
              <Tooltip title={channelType === 'email' ? "Filter emails" : "Filter threads"}>
                <IconButton
                  onClick={onFilterClick}
                  size="small"
                  sx={{
                    color: activeFilterCount > 0 
                      ? muiTheme.palette.primary.main 
                      : muiTheme.palette.text.secondary,
                    backgroundColor: activeFilterCount > 0 
                      ? muiTheme.palette.primary.light + '20' 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: muiTheme.palette.action.hover,
                    },
                  }}
                >
                  <Badge 
                    badgeContent={activeFilterCount > 0 ? activeFilterCount : 0} 
                    color="primary"
                    sx={{
                      // Ensure badge text is white in both light and dark themes
                      '& .MuiBadge-badge': {
                        color: '#ffffff', // White text color
                        fontWeight: 'bold', // Make text more visible
                      },
                    }}
                  >
                    <FilterIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* Bulk “select all” row: same horizontal inset + checkbox sizing as ListItem rows (no FormControlLabel — it mis-sizes/pads the control) */}
      {showBulkSelectAllRow && bulkSelectAllCheckbox && onBulkSelectAllVisible && (
        <Box
          sx={{
            // Match ListItem p: 1.5 so the checkbox column lines up with thread rows
            p: 1.5,
            borderBottom: `1px solid ${muiTheme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            backgroundColor: muiTheme.palette.background.paper,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Checkbox
              size="small"
              checked={bulkSelectAllCheckbox.checked}
              indeterminate={bulkSelectAllCheckbox.indeterminate}
              onChange={() => onBulkSelectAllVisible()}
              sx={{ p: 0.25, flexShrink: 0 }}
              inputProps={{ 'aria-label': 'Select all threads' }}
            />
            {/* Match email thread first-line typography (sender): bold primary, same scale as list rows */}
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.2,
              }}
            >
              Select all
            </Typography>
          </Box>
        </Box>
      )}

      {/* Thread List - Scrollable; scroll at top on load/category switch; "Loading more threads..." inline at bottom when user scrolls down and load-more API runs */}
      <Box ref={scrollContainerRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {threadsLoading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            p: 3,
            textAlign: 'center'
          }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading threads...
            </Typography>
          </Box>
        ) : uniqueThreads.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            p: 3,
            textAlign: 'center'
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {(activeFilterCount > 0 || isViewingArchivedThreads)
                ? 'No threads match your filters'
                : channelType === 'email'
                ? 'No email conversations'
                : channelType === 'message'
                ? 'No messages'
                : 'No voice calls'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(activeFilterCount > 0 || isViewingArchivedThreads)
                ? 'Try adjusting or clearing filters to see more results.'
                : channelType === 'email'
                ? 'Conversations will appear here once you start receiving.'
                : channelType === 'message'
                ? 'Messages will appear here once you start receiving.'
                : 'Voice calls will appear here once you start receiving.'}
            </Typography>
          </Box>
        ) : displayThreads.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No threads match your search
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a different term or clear the search box to see all threads.
            </Typography>
          </Box>
        ) : (
          <>
          <List sx={{ p: 0 }}>
            {displayThreads.map((thread: ApiThread, index: number) => {
              // Active thread: light blue (light) / subtle primary (dark); unread: light grey (light) / dark grey (dark); read: transparent
              const isActive = selectedThreadId === thread.id;
              const isUnread = thread.is_read === false;
              const isDark = muiTheme.palette.mode === 'dark';
              // Theme-aware active and unread backgrounds so text stays readable in dark mode
              const activeBg = isDark
                ? (muiTheme.palette.primary.main + '20') // subtle primary tint in dark mode
                : '#e3f2fd';
              const unreadBg = isDark
                ? muiTheme.palette.grey[800]
                : muiTheme.palette.grey[50];
              const unreadHoverBg = isDark
                ? muiTheme.palette.grey[700]
                : muiTheme.palette.grey[100];
              const archivedActiveBg = isDark
                ? muiTheme.palette.grey[700] + '40'
                : muiTheme.palette.grey[400] + '20';
              const archivedInactiveBg = isDark
                ? muiTheme.palette.grey[900]
                : muiTheme.palette.grey[100];
              return (
              <ListItem
                key={thread.id}
                draggable={channelType === 'email' && !!channelId}
                onDragStart={(e) => {
                  if (channelType !== 'email' || !channelId) return;
                  e.dataTransfer.setData(THREAD_MOVE_DRAG_TYPE, JSON.stringify({
                    threadId: thread.id,
                    channelId,
                    external_app_id: thread.external_app_id ?? null,
                  }));
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => onThreadSelect(thread)}
                sx={{
                  borderBottom: index === displayThreads.length - 1 ? 'none' : `1px solid ${muiTheme.palette.divider}`,
                  // Active: theme-aware; unread: theme-aware; read: transparent
                  backgroundColor: isActive
                    ? activeBg
                    : isUnread
                      ? unreadBg
                      : 'transparent',
                  cursor: 'pointer',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  p: 1.5,
                  '&:hover': {
                    backgroundColor: isActive
                      ? activeBg
                      : isUnread
                        ? unreadHoverBg
                        : muiTheme.palette.action.hover,
                  },
                  // Expand checkbox column on row hover when bulk selection is empty (column stays width 0 until then)
                  ...(!anyBulkThreadSelected && {
                    '&:hover .thread-list-bulk-cb': {
                      opacity: 1,
                      pointerEvents: 'auto',
                      maxWidth: BULK_CHECKBOX_COLUMN_WIDTH_PX,
                      marginRight: 1, // matches spacing unit used when bulk selection is active
                    },
                  }),
                  // When viewing archive list, use same styling as inbox; only apply dull styling for archived threads shown in inbox (edge case)
                  ...(thread.is_archived && !showArchivedThreads && {
                    opacity: 0.6,
                    backgroundColor: isActive
                      ? archivedActiveBg
                      : archivedInactiveBg,
                  }),
                }}
              >
                {/* First row: (avatar) [email: from email bold | message: subject] [time][menu] */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                  {/* Outer row: no flex gap before avatar — avoids empty gutter when checkbox column is collapsed */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    {/* Bulk checkbox: admin only; column width 0 when hidden so avatar aligns with rows that have no checkbox */}
                    {isChannelAdmin && onBulkToggle && (
                      <Box
                        className="thread-list-bulk-cb"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          flexShrink: 0,
                          minWidth: 0,
                          overflow: 'hidden',
                          maxWidth: anyBulkThreadSelected
                            ? BULK_CHECKBOX_COLUMN_WIDTH_PX
                            : 0,
                          marginRight: anyBulkThreadSelected ? 1 : 0,
                          opacity: anyBulkThreadSelected ? 1 : 0,
                          pointerEvents: anyBulkThreadSelected ? 'auto' : 'none',
                          transition: muiTheme.transitions.create(
                            ['opacity', 'max-width', 'margin'],
                            { duration: muiTheme.transitions.duration.shorter }
                          ),
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={bulkSelectedIds.includes(thread.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(_, checked) => onBulkToggle(thread.id, checked)}
                          sx={{ p: 0.25, flexShrink: 0 }}
                          inputProps={{ 'aria-label': `Select thread ${thread.title}` }}
                        />
                      </Box>
                    )}
                    {/* Avatar + titles: inner gap preserves spacing between avatar and text when checkbox is collapsed */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                    {/* Avatar: from from_email_id for email, from title for message channels */}
                    <Avatar
                      sx={{
                        bgcolor: getAvatarColor(channelType === 'email' ? (thread.from_email_id || thread.title) : thread.title),
                        width: 24,
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        flexShrink: 0,
                      }}
                    >
                      {getFirstAlphabeticChar(channelType === 'email' ? (thread.from_email_id || thread.title) : thread.title)}
                    </Avatar>
                    {/* Email: from email ID in bold; Message: subject (bold when unread/active) */}
                    {channelType === 'email' ? (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                          fontSize: '0.8125rem',
                        }}
                        title={thread.from_email_id || thread.title}
                      >
                        {thread.from_email_id || thread.title}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive || isUnread ? 'bold' : 'normal',
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                          fontSize: '0.8125rem',
                        }}
                        title={thread.title}
                      >
                        {capitalizeFirstLetter(thread.title)}
                      </Typography>
                    )}
                    {/* Show Archived chip for archived threads */}
                    {(thread.is_archived || showArchivedThreads) && (
                      <Chip
                        label="Archived"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          // Theme-aware so chip is readable in dark mode
                          backgroundColor: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[600] : muiTheme.palette.grey[300],
                          color: muiTheme.palette.mode === 'dark' ? muiTheme.palette.grey[200] : muiTheme.palette.grey[700],
                          fontWeight: 'medium',
                          flexShrink: 0,
                          marginRight: '5px',
                        }}
                      />
                    )}
                  </Box>
                  </Box>
                  <Tooltip 
                    title={formatDetailedTooltip(thread.last_message_at? thread.last_message_at : thread.updated_at)}
                    placement="top"
                    arrow
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {formatRelativeTime(thread.last_message_at? thread.last_message_at : thread.updated_at)}
                      </Typography>
                      {isChannelAdmin && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, thread)}
                          sx={{
                            padding: '2px',
                          '&:hover': {
                            backgroundColor: muiTheme.palette.action.hover,
                          },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                      )}
                    </Box>
                  </Tooltip>
                </Box>

                {/* Email only: second row = subject (label shown only in email content section) */}
                {channelType === 'email' && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isActive || isUnread ? 'bold' : 500,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                      fontSize: '0.8125rem',
                      lineHeight: 1.2,
                      mb: 0.75,
                    }}
                    title={thread.title}
                  >
                    {capitalizeFirstLetter(thread.title)}
                  </Typography>
                )}

                {/* Last row: one-line content (email = third row, message = second row) */}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    fontSize: channelType === 'email' ? '0.7rem' : '0.75rem',
                    lineHeight: 1.2,
                    color: 'text.secondary',
                    fontWeight: 400,
                  }}
                >
                  {thread.description || 'No preview available...'}
                </Typography>
              </ListItem>
            );
            })}
          </List>

          {/* "Loading more threads..." at bottom of list – visible when user scrolls down and load-more API runs (scroll stays where user scrolled) */}
          {loadingMore && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 2,
                minHeight: 56,
                flexShrink: 0,
                borderTop: `1px solid ${muiTheme.palette.divider}`,
                backgroundColor: muiTheme.palette.background.paper,
              }}
            >
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Loading more threads...
              </Typography>
            </Box>
          )}
          </>
        )}
      </Box>

      {/* Thread Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 120,
            boxShadow: 2,
          },
        }}
      >
        {isChannelAdmin && (
          <MenuItem onClick={handleEdit}>
            <MenuListItemIcon>
              <EditIcon fontSize="small" />
            </MenuListItemIcon>
            Edit
          </MenuItem>
        )}
        {isChannelAdmin && (
          <MenuItem onClick={handleArchive}>
            <MenuListItemIcon>
              {(selectedThread?.is_archived || showArchivedThreads || isViewingArchivedThreads) ? (
                <UnarchiveIcon fontSize="small" />
              ) : (
                <ArchiveIcon fontSize="small" />
              )}
            </MenuListItemIcon>
            {(selectedThread?.is_archived || showArchivedThreads || isViewingArchivedThreads) ? 'Unarchive' : 'Archive'}
          </MenuItem>
        )}
        {/* Move Thread: only for regular provider (exclude WhatsApp, Slack, etc.) */}
        {channelType === 'message' && messagingProvider === 'regular' && onThreadMove && isChannelAdmin && (
          <MenuItem onClick={handleMoveThread}>
            <MenuListItemIcon>
              <MoveThreadIcon fontSize="small" />
            </MenuListItemIcon>
            Move Thread
          </MenuItem>
        )}
        {isChannelAdmin && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <MenuListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </MenuListItemIcon>
            Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ThreadList;
